import React, { useEffect, useState } from 'react';
import { Popper, ClickAwayListener, Fade, Box, Paper } from '@mui/material';
import Tooltip from './Tooltip';

/**
 * Компонент Popper для неблокирующих шагов туториала, который позиционируется относительно элемента
 *
 * @param {Object} props - свойства компонента
 * @param {boolean} props.open - открыт ли поппер
 * @param {HTMLElement} props.anchorEl - элемент, к которому привязан поппер
 * @param {string} props.title - заголовок шага
 * @param {string} props.content - содержимое шага
 * @param {string} props.placement - расположение подсказки
 * @param {function} props.onNext - обработчик нажатия кнопки "Далее"
 * @param {function} props.onPrev - обработчик нажатия кнопки "Назад"
 * @param {function} props.onSkip - обработчик нажатия кнопки "Пропустить"
 * @param {boolean} props.showPrev - показывать ли кнопку "Назад"
 * @param {boolean} props.isLastStep - последний ли это шаг
 * @param {number} props.currentStep - текущий шаг
 * @param {number} props.totalSteps - общее количество шагов
 */

// Поддерживаемые значения placement см. https://mui.com/material-ui/api/popper/
// 'top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end', 'right-start', 'right-end', 'left-start', 'left-end', 'auto', 'auto-start', 'auto-end', 'center'

const TutorialPopper = ({
  open,
  anchorEl,
  title,
  content,
  placement = 'center',
  onNext,
  onPrev,
  onSkip,
  showPrev,
  isLastStep,
  currentStep,
  totalSteps,
}) => {
  const [popperPlacement, setPopperPlacement] = useState(placement);

  useEffect(() => {
    if (placement === 'center') {
      // Автоматически определяем top/bottom в зависимости от положения anchorEl
      let autoPlacement = 'top';
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        const centerY = rect.top + rect.height / 2;
        const viewportHeight = window.innerHeight;
        if (centerY < viewportHeight / 2) {
          autoPlacement = 'bottom';
        }
      }
      setPopperPlacement(autoPlacement);
    } else {
      setPopperPlacement(placement);
    }
  }, [placement, anchorEl]);

  // Если нет целевого элемента, ничего не отображаем
  if (!anchorEl || !open) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={popperPlacement}
      transition
      style={{ zIndex: 20000 }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 16], // увеличенное смещение [x, y] от таргета
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: window,
            altAxis: true, // предотвращает выход за границы по обеим осям
            padding: 24, // больший отступ от границ
            altBoundary: true,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'bottom', 'right', 'left'],
            padding: 24, // отступ для переключения
            rootBoundary: 'viewport',
          },
        },
        {
          name: 'computeStyles',
          options: {
            gpuAcceleration: true,
            adaptive: true,
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={300}>
          <Paper
            elevation={0}
            sx={{
              width: {
                xs: 'calc(100vw - 32px)', // на мобильных почти на всю ширину
                sm: 360,
              },
              maxWidth: 'calc(100vw - 40px)',
              borderRadius: '16px',
              p: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              position: 'relative',
            }}
          >
            <ClickAwayListener onClickAway={() => {}}>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Tooltip
                  title={title}
                  content={content}
                  position={{ top: 0, left: 0 }}
                  placement={placement}
                  onNext={onNext}
                  onPrev={onPrev}
                  onSkip={onSkip}
                  showPrev={showPrev}
                  showNext={true}
                  isLastStep={isLastStep}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                />
              </Box>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

export default TutorialPopper;
